import { pythonApi } from '../../../app/services/api/api'
import { AutomationQueryParams, ManualControlDevice, ManualControlGroup } from './types'

const extendedApi = pythonApi.injectEndpoints({
  endpoints: (builder) => ({
    toggleDeviceById: builder.mutation<any, { id: number; action: string }>({
      query: ({ id, action }) => ({
        url: `v2/toggle-relay-device/${id}/`,
        method: 'POST',
        body: { action },
      }),
    }),
    toggleDevice: builder.mutation<any, ManualControlDevice>({
      query: (body) => ({
        url: `manual_control/devices-status`,
        method: 'POST',
        body: body,
      }),
    }),
    toggleGroup: builder.mutation<any, ManualControlGroup>({
      query: ({id, body}) => ({
        url: `manual_control/group/${id}/`,
        method: 'POST',
        body: body,
      }),
    }),
    getAllDevices: builder.query<any, void>({
      query: () => ({
        url: `/devices-model-type/`,
        method: 'GET',
      }),
    }),
    createDeviceAutomation: builder.mutation<any, AutomationQueryParams>({
      query: (body) => ({
        url: `/automation/`,
        method: 'POST',
        body: body,
      }),
      invalidatesTags: ['ListDevices', 'ListDevicesAutomation'],
    }),
    createGroupAutomation: builder.mutation<any, AutomationQueryParams>({
      query: (body) => ({
        url: `/group-automation/`,
        method: 'POST',
        body: body,
      }),
      invalidatesTags: ['ListGroups', 'ListGroupsAutomation'],
    }),
    updateDeviceAutomation: builder.mutation<
      any,
      { id: string; body: AutomationQueryParams }
    >({
      query: ({ id, body }) => ({
        url: `/automation/${id}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['ListDevices', 'ListDevicesAutomation'],
    }),
    updateGroupAutomation: builder.mutation<
      any,
      { id: string; body: AutomationQueryParams }
    >({
      query: ({ id, body }) => ({
        url: `/group-automation/${id}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['ListGroups', 'ListGroupsAutomation'],
    }),
    listDeviceAutomations: builder.query<
      any,
      { device?: number; location?: number; group?: number; page?: number; search?: string }
    >({
      query: ({ device, location, group, page, search }) => {
        const params = new URLSearchParams()
        if (device) {
          params.append('device', device.toString())
        }
        if (location) {
          params.append('location', location.toString())
        }
        if (group) {
          params.append('group', group.toString())
        }
        if (page) {
          params.append('page', page.toString())
        }
        if (search) {
          params.set('search', search)
        }

        return {
          url: `/automation/?${params.toString()}`,
          method: 'GET',
        }
      },
      providesTags: ['ListDevicesAutomation'],
    }),
    listGroupAutomations: builder.query<
      any,
      { device?: number; location?: number; group?: number; page?: number; search?: string }
    >({
      query: ({ device, location, group, page, search }) => {
        const params = new URLSearchParams()
        if (device) {
          params.append('device', device.toString())
        }
        if (location) {
          params.append('location', location.toString())
        }
        if (group) {
          params.append('group', group.toString())
        }
        if (page) {
          params.append('page', page.toString())
        }
        if (search) {
          params.set('search', search)
        }

        return {
          url: `/group-automation/?${params.toString()}`,
          method: 'GET',
        }
      },
      providesTags: ['ListGroupsAutomation'],
    }),
    deleteGroupAutomation: builder.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `/group-automation/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ListGroups', 'ListGroupsAutomation'],
    }),
    deleteDeviceAutomation: builder.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `/automation/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ListDevices', 'ListDevicesAutomation'],
    }),
    getGroupsData: builder.query<any, any>({
      query: (props) => {
        const searchParams = new URLSearchParams()
        if (props.groups) {
          searchParams.set('groups', props.groups)
        }
        if (props.locations) {
          searchParams.set('locations', props.locations)
        }
        if (props.devices) {
          searchParams.set('devices', props.devices)
        }
        if (props.from_date) {
          searchParams.set('from_date', props.from_date)
        }
        if (props.to_date) {
          searchParams.set('to_date', props.to_date)
        }
        if (props.latest) {
          searchParams.set('latest', props.latest)
        }
        if (props.search) {
          searchParams.set('search', props.search)
        }
        if (props.page) {
          searchParams.set('page', props.page)
        }
        if (props.page_size) {
          searchParams.set('page_size', props.page_size)
        }
        if (props.has_automation) {
          searchParams.set('has_automation', props.has_automation)
        }

        return {
          url: `/group-with-automation/?${searchParams.toString()}`,
          method: 'GET',
        }
      },
      providesTags: ['ListGroups'],
    }),
    getDevicesWithAutomations: builder.query<any, any>({
      query: (props) => {
        const searchParams = new URLSearchParams()
        if (props.location) {
          searchParams.set('locations', props.location)
        }
        if (props.groups) {
          searchParams.set('groups', props.groups)
        }

        return {
          url: `/devices-with-automation/?${searchParams.toString()}`,
          method: 'GET',
        }
      },
      providesTags: ['ListGroups'],
    }),
    getDevicesData: builder.query<any, any>({
      query: (props) => {
        const searchParams = new URLSearchParams()
        if (props.groups) {
          searchParams.set('groups', props.groups)
        }
        if (props.locations) {
          searchParams.set('locations', props.locations)
        }
        if (props.devices) {
          searchParams.set('devices', props.devices)
        }
        if (props.from_date) {
          searchParams.set('from_date', props.from_date)
        }
        if (props.to_date) {
          searchParams.set('to_date', props.to_date)
        }
        if (props.latest) {
          searchParams.set('latest', props.latest)
        }
        if (props.search) {
          searchParams.set('search', props.search)
        }
        if (props.page) {
          searchParams.set('page', props.page)
        }
        if (props.page_size) {
          searchParams.set('page_size', props.page_size)
        }
        if (props.has_automation) {
          searchParams.set('has_automation', props.has_automation)
        }

        return {
          url: `/device-automation/?${searchParams.toString()}`,
          method: 'GET',
        }
      },
      providesTags: ['ListDevices'],
    }),
    getDeviceTopicCommand: builder.query<any, {topic?: number}>({
      query: (params) => {
        return {
          url: `/device-topic-command/`,
          method: 'GET',
          params
        }
      },
    }),
  }),
  overrideExisting: false,
})

export const {
  useGetDevicesWithAutomationsQuery,
  useGetAllDevicesQuery,
  useToggleDeviceByIdMutation,
  useGetGroupsDataQuery,
  useGetDevicesDataQuery,
  useCreateDeviceAutomationMutation,
  useCreateGroupAutomationMutation,
  useListDeviceAutomationsQuery,
  useUpdateGroupAutomationMutation,
  useListGroupAutomationsQuery,
  useDeleteGroupAutomationMutation,
  useDeleteDeviceAutomationMutation,
  useUpdateDeviceAutomationMutation,
  useToggleDeviceMutation,
  useToggleGroupMutation,
  useGetDeviceTopicCommandQuery,
} = extendedApi
